// src/components/TokenExpirationTimer.js
import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import logger from "../utils/logger";
import "./TokenExpirationTimer.css";

const STORAGE_KEYS = {
  EXPIRES_AT: "auth_expires_at",
};

const TokenExpirationTimer = () => {
  const { logout, getAccessToken } = useAuth();
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [isExpiringSoon, setIsExpiringSoon] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const calculateTimeRemaining = useCallback(() => {
    const expiresAt = localStorage.getItem(STORAGE_KEYS.EXPIRES_AT);
    if (!expiresAt) return null;

    const expiresAtMs = parseInt(expiresAt, 10);
    const now = Date.now();
    const remainingMs = expiresAtMs - now;

    if (remainingMs <= 0) {
      return 0;
    }

    return Math.floor(remainingMs / 1000); // Convert to seconds
  }, []);

  const formatTime = (seconds) => {
    if (seconds === null) return "--:--";
    if (seconds <= 0) return "00:00";

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}:${String(minutes).padStart(2, "0")}:${String(
        secs
      ).padStart(2, "0")}`;
    }
    return `${String(minutes).padStart(2, "0")}:${String(secs).padStart(
      2,
      "0"
    )}`;
  };

  const handleRefreshToken = useCallback(async () => {
    if (!isExpiringSoon || isRefreshing) return;

    setIsRefreshing(true);
    try {
      logger.log("[TokenExpirationTimer] Manually refreshing token...");
      await getAccessToken();
      logger.log("[TokenExpirationTimer] Token refreshed successfully");
    } catch (error) {
      logger.error("[TokenExpirationTimer] Failed to refresh token:", error);
    } finally {
      setIsRefreshing(false);
    }
  }, [isExpiringSoon, isRefreshing, getAccessToken]);

  useEffect(() => {
    // Initial calculation
    const remaining = calculateTimeRemaining();
    setTimeRemaining(remaining);

    // Update every second
    const intervalId = setInterval(() => {
      const remaining = calculateTimeRemaining();
      setTimeRemaining(remaining);

      // Check if expiring soon (less than 5 minutes)
      if (remaining !== null && remaining > 0 && remaining <= 300) {
        setIsExpiringSoon(true);
      } else {
        setIsExpiringSoon(false);
      }

      // Force logout when timer reaches 0
      if (remaining === 0) {
        logger.warn("[TokenExpirationTimer] Token expired. Forcing logout...");
        clearInterval(intervalId);
        logout("token_expired");
      }
    }, 1000);

    return () => clearInterval(intervalId);
  }, [calculateTimeRemaining, logout]);

  // Listen for storage changes and custom tokenRefreshed event
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === STORAGE_KEYS.EXPIRES_AT) {
        const remaining = calculateTimeRemaining();
        setTimeRemaining(remaining);
        logger.log(
          "[TokenExpirationTimer] Token refreshed (storage), timer updated:",
          remaining,
          "seconds remaining"
        );
      }
    };

    const handleTokenRefresh = (e) => {
      const remaining = calculateTimeRemaining();
      setTimeRemaining(remaining);
      logger.log(
        "[TokenExpirationTimer] Token refreshed (event), timer updated:",
        remaining,
        "seconds remaining"
      );
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("tokenRefreshed", handleTokenRefresh);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("tokenRefreshed", handleTokenRefresh);
    };
  }, [calculateTimeRemaining]);

  if (timeRemaining === null) {
    return null; // Don't show timer if no expiration data
  }

  const tooltipText = isExpiringSoon
    ? "Click to refresh login session"
    : `Session expires in ${formatTime(timeRemaining)}`;

  return (
    <div
      className={`token-expiration-timer ${
        isExpiringSoon ? "expiring-soon" : ""
      } ${isExpiringSoon ? "clickable" : ""} ${
        isRefreshing ? "refreshing" : ""
      }`}
      title={tooltipText}
      onClick={handleRefreshToken}
      style={isExpiringSoon ? { cursor: "pointer" } : undefined}
    >
      <span className="timer-icon">⏱️</span>
      <span className="timer-text">{formatTime(timeRemaining)}</span>
    </div>
  );
};

export default TokenExpirationTimer;
